import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QRPoster() {
  const [sc, setSC] = useState(null);
  const appUrl = window.location.origin;
  const requestUrl = `${appUrl}/request`;

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSC(r.data)).catch(() => {});
    axios.post(`${API}/analytics/event`, { page: "qr-poster", referrer: document.referrer }).catch(() => {});
  }, []);

  const companyName = sc?.name || "XSTRONG POWER EV CHARGER";

  return (
    <>
      {/* ===== PRINT TOOLBAR (hidden when printing) ===== */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <img src="/logo.jpg" alt="logo" style={{ height: "36px", width: "auto", objectFit: "contain" }} />
          <div>
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
              {companyName}
            </p>
            <p className="text-xs text-slate-400">QR Code Poster — print &amp; display at your counter</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.history.back()}
            className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 border border-slate-200 rounded-lg"
          >
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors"
            data-testid="qr-poster-print-button"
          >
            🖨️ Print Poster
          </button>
        </div>
      </div>

      {/* ===== POSTER CONTENT ===== */}
      <div
        style={{
          paddingTop: "72px",
          background: "#f8fafb",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        className="no-print-padding"
      >
        {/* A4 Poster */}
        <div
          data-testid="qr-poster-root"
          style={{
            width: "794px",
            minHeight: "1000px",
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 4px 40px rgba(0,0,0,0.10)",
            overflow: "hidden",
            fontFamily: "'Inter', sans-serif",
            position: "relative",
          }}
        >
          {/* Green top band */}
          <div style={{ background: "#16a34a", padding: "32px 40px 28px", textAlign: "center" }}>
          <div style={{ background: "#ffffff", borderRadius: "12px", padding: "8px 16px", display: "inline-block", marginBottom: "16px" }}>
            <img src="/logo.jpg" alt="logo" style={{ height: "64px", width: "auto", objectFit: "contain" }} />
          </div>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "28px", fontWeight: 800, color: "#ffffff", margin: "0 0 6px", letterSpacing: "-0.02em" }}>
              {companyName}
            </h1>
            <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.85)", margin: 0 }}>
              {sc?.tagline || "EV Charger Repair & Service Center"}
            </p>
          </div>

          {/* Main content */}
          <div style={{ padding: "36px 48px", textAlign: "center" }}>

            {/* Headline */}
            <div style={{ marginBottom: "28px" }}>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "26px", fontWeight: 800, color: "#1e293b", margin: "0 0 10px", lineHeight: 1.2 }}>
                Submit Your Charger<br />Repair Request Online
              </h2>
              <p style={{ fontSize: "15px", color: "#64748b", margin: 0 }}>
                Scan the QR code below with your phone camera
              </p>
            </div>

            {/* QR Code — large and prominent */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
              <div style={{
                background: "#f0fdf4",
                border: "3px solid #16a34a",
                borderRadius: "24px",
                padding: "28px",
                display: "inline-block",
                boxShadow: "0 8px 32px rgba(22,163,74,0.15)"
              }}>
                <QRCodeSVG
                  value={requestUrl}
                  size={220}
                  level="M"
                  includeMargin={false}
                  fgColor="#1e293b"
                  bgColor="transparent"
                />
                <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: "#64748b", marginTop: "12px", marginBottom: 0, letterSpacing: "0.04em" }}>
                  {requestUrl}
                </p>
              </div>
            </div>

            {/* Step-by-step instructions */}
            <div style={{ display: "flex", gap: "16px", marginBottom: "32px", justifyContent: "center" }}>
              {[
                { num: "1", icon: "📱", title: "Scan QR Code", desc: "Open your phone camera and point at the QR code" },
                { num: "2", icon: "📝", title: "Fill Details", desc: "Enter your name, phone, charger info & problem" },
                { num: "3", icon: "✅", title: "Get Job Number", desc: "Receive your unique job number instantly" },
              ].map(step => (
                <div key={step.num} style={{
                  flex: 1,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  padding: "18px 14px",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "28px", marginBottom: "8px" }}>{step.icon}</div>
                  <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "22px", height: "22px", background: "#16a34a", color: "#fff", borderRadius: "50%", fontSize: "11px", fontWeight: 700, marginBottom: "8px" }}>
                    {step.num}
                  </div>
                  <p style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", margin: "0 0 4px", fontFamily: "'Space Grotesk', sans-serif" }}>{step.title}</p>
                  <p style={{ fontSize: "11px", color: "#64748b", margin: 0, lineHeight: 1.4 }}>{step.desc}</p>
                </div>
              ))}
            </div>

            {/* Track existing job */}
            <div style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "12px",
              padding: "14px 20px",
              marginBottom: "28px",
              display: "flex",
              alignItems: "center",
              gap: "12px"
            }}>
              <span style={{ fontSize: "24px" }}>🔍</span>
              <div style={{ textAlign: "left" }}>
                <p style={{ fontSize: "13px", fontWeight: 700, color: "#15803d", margin: "0 0 2px" }}>Already submitted? Track your repair</p>
                <p style={{ fontSize: "12px", color: "#64748b", margin: 0, fontFamily: "monospace" }}>{appUrl}/track</p>
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: "1px solid #e2e8f0", marginBottom: "20px" }} />

            {/* Contact info */}
            <div style={{ display: "flex", justifyContent: "center", gap: "32px", flexWrap: "wrap" }}>
              {sc?.phone && (
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "20px" }}>📞</span>
                  <p style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", margin: "4px 0 0" }}>{sc.phone}{sc.phone2 ? ` / ${sc.phone2}` : ""}</p>
                  <p style={{ fontSize: "11px", color: "#94a3b8", margin: "2px 0 0" }}>Call us</p>
                </div>
              )}
              {sc?.email && (
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "20px" }}>✉️</span>
                  <p style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", margin: "4px 0 0" }}>{sc.email}</p>
                  <p style={{ fontSize: "11px", color: "#94a3b8", margin: "2px 0 0" }}>Email us</p>
                </div>
              )}
              {(sc?.address || sc?.city) && (
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "20px" }}>📍</span>
                  <p style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", margin: "4px 0 0" }}>
                    {[sc?.city, sc?.state].filter(Boolean).join(", ")}
                  </p>
                  <p style={{ fontSize: "11px", color: "#94a3b8", margin: "2px 0 0" }}>{sc?.address}</p>
                </div>
              )}
              {sc?.working_hours && (
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "20px" }}>🕐</span>
                  <p style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", margin: "4px 0 0" }}>{sc.working_hours}</p>
                  <p style={{ fontSize: "11px", color: "#94a3b8", margin: "2px 0 0" }}>Working hours</p>
                </div>
              )}
            </div>
          </div>

          {/* Green bottom strip */}
          <div style={{ background: "#f0fdf4", borderTop: "2px solid #bbf7d0", padding: "14px 40px", textAlign: "center" }}>
            <p style={{ fontSize: "11px", color: "#15803d", margin: 0, fontWeight: 500 }}>
              {companyName} — Scan. Submit. Track. Fast &amp; Easy EV Charger Service.
            </p>
          </div>
        </div>

        <div style={{ height: "40px" }} className="no-print" />
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 8mm; }
          .no-print { display: none !important; }
          body { background: white !important; }
          div[data-testid="qr-poster-root"] {
            width: 100% !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            min-height: unset !important;
          }
        }
      `}</style>
    </>
  );
}
